import { RTCClient } from 'webrtc';
import {
  selectSelectedPair,
  statsEntries,
  summarizeInboundAudio,
  summarizeInboundBytes,
  summarizeInboundVideo,
  type IInboundAudio,
  type IInboundVideo,
  type TRawStat,
} from 'telemetry/rtcStats';
import {
  recordVoipPeerCount,
  recordVoipPeerSample,
  recordVoipStatsReadFailure,
  type IVoipPeerSample,
} from 'state/session/voipQuality';
import type { IDeadReceiverEvidence, IStalledVideoEvidence } from 'webrtc/types';
import {
  inboundCountersByTrack,
  mutedReceiverTrackIds,
  reportedMutedKinds,
  risingMutedKinds,
  type IMutedReceiverTracks,
} from 'utils/ui/webkit';

/**
 * Prod-safe per-peer `getStats()` sampler feeding the session-scoped VOIP quality counters
 * (AVS-4419, `state/session/voipQuality.ts`).
 *
 * ## Why this exists beside the recorder's sampler
 *
 * `telemetry/rtcStats.ts` already samples `getStats()` — for the Session Flight Recorder, which is
 * built only for dev and prelive (`scripts/recorder-build-gate.mjs`) and reads only while
 * `isRecording()`. The analytics counters this feeds ship from prod, where no recorder exists, so
 * they need a reader that is always present. It reuses the recorder's import-free, pure projection
 * (`selectSelectedPair`, `summarizeInboundVideo`) rather than re-implementing the four-way
 * selected-pair fallback — the same precedent as `utils/web-component.tsx` importing the
 * import-free `telemetry/recorder/gate`. Nothing here imports the recorder itself: the build check
 * greps the built artefact for a sentinel that lives only in `telemetry/recorder/core.ts`.
 *
 * On dev/prelive the two samplers run side by side (≈2N reads per ~4 s on an N-peer call). Accepted:
 * `getStats()` is a cheap asynchronous read, and consulting the recorder from here would drag its
 * sentinel into the prod bundle.
 *
 * ## The silent-transport watchdog (AVS-4441)
 *
 * The same read that feeds the counters also answers "did any byte arrive since last tick". A
 * transport that reports `connected` while delivering nothing is invisible to every state-machine
 * ladder in `webrtc/index.ts` — the prod failure delivered 10 frames and then froze with ICE
 * connected, 54 ms RTT and zero loss. After `VOIP_SILENT_TICKS_BEFORE_ESCALATION` motionless ticks
 * the sampler calls `onSilentTransport`, and the client decides what to do about it. This module
 * stays a measurement: it never restarts ICE and never writes the `transportFailed` flag.
 *
 * ## The stalled-video watchdog (AVS-4458)
 *
 * The byte sum above is blind to one pair failing while the other flows. Prelive 2026-09-16: audio
 * played for ten minutes over gray tiles — video packets kept arriving, not one frame decoded, and
 * the audio bytes kept the sum moving. So the same read also tracks the VIDEO kind on its own:
 * camera on, video packets moving, frames decoded flat. After
 * `VOIP_STALLED_VIDEO_TICKS_BEFORE_ESCALATION` such ticks the sampler calls `onStalledVideo`, and
 * `onVideoRecovered` once frames decode again after that. Same rule as the silent ladder: this
 * module measures, the client acts.
 *
 * ## Cost and privacy
 *
 * N `getStats()` reads per `VOIP_STATS_INTERVAL_MS`, sequential, one per live peer. A peer the
 * recovery ladder wrote off (`transportFailed`) is counted but never read, so a dead peer costs
 * nothing — the ticket's N-not-N² rule. The projection keeps numbers and booleans only: the
 * selected pair's raw addresses and TURN url (`ISelectedPair`) never leave `projectVoipPeerSample`.
 *
 * ## Lifecycle
 *
 * `setTimeout`-rearmed, never `setInterval`, so a pass that outruns its interval cannot stack.
 * Armed from `initWebRtc` after a successful connect and re-armed on every `peer:connected`. Every
 * tick re-resolves `RTCClient.current()` — never a cached client, because `destroy()` nulls the
 * singleton and a takeover rebuilds it — stops itself when there is no client, and parks after
 * `VOIP_STATS_IDLE_TICKS_BEFORE_PARK` peerless ticks. Customers never emit `session:end`
 * (`utils/ui/rtc.ts`), so on their side the stop is the client going away or the idle budget.
 */

/** Healthy cadence. The ticket asks for ~5 s per peer; the recorder's 2-5 s window agrees. */
export const VOIP_STATS_INTERVAL_MS = 5_000;

/** Consecutive peerless ticks after which the loop parks itself until the next `peer:connected`. */
export const VOIP_STATS_IDLE_TICKS_BEFORE_PARK = 3;

/**
 * Consecutive ticks with NO inbound byte movement that make a transport silent (AVS-4441).
 *
 * The first tick of a window is only a baseline — it has nothing to compare against — so the
 * escalating tick is the (window + 1)th: 20 s after arming, 15 s of observed silence. Long enough
 * that a stutter, a paused track, or one slow read never spends a rung of the recovery ladder.
 */
export const VOIP_SILENT_TICKS_BEFORE_ESCALATION = 3;

/**
 * Consecutive ticks with video packets arriving and NO frame decoded that make a peer's video
 * stalled (AVS-4458). The same baseline-plus-window shape as the silent ladder: the first
 * escalating tick is the (window + 1)th, 20 s after the stall began. That tick is the next
 * window's baseline, so each later escalation comes 15 s after the one before.
 */
export const VOIP_STALLED_VIDEO_TICKS_BEFORE_ESCALATION = 3;

/**
 * Consecutive ticks a WebKit receiver track stays `muted` while its media keeps arriving that make
 * the link dead (AVS-4484). The first tick is the baseline, so the escalating tick is the
 * (window + 1)th: up to 15 s after the failure began.
 */
export const VOIP_DEAD_RECEIVER_TICKS_BEFORE_ESCALATION = 2;

/** One peer, as the sampler needs to see it. Read live per tick — never cached by this module. */
export interface IVoipStatsPeer {
  connectionId: string;
  peerConnection: RTCPeerConnection;
  /** The recovery ladder wrote this transport off — counted, never read. */
  transportFailed: boolean;
  /** `stream != null` on the store entry — `ontrack` fired. */
  hasStream: boolean;
  /** The signaled `audioMuted` flag is not `true`. */
  audioExpected: boolean;
  /** The signaled `videoMuted` flag is not `true`. */
  videoExpected: boolean;
  /**
   * AVS-4484: the ids of the live remote tracks whose `muted` flag is true. Only ever filled on WebKit,
   * where a healthy receiver is never muted while media flows; Chromium reports `muted:true` on tracks
   * that paint fine, so it always reads empty there. Ids, so each muted track is judged by its own
   * inbound-rtp counter and not by a healthy receiver of the same kind.
   */
  mutedTracks: IMutedReceiverTracks;
}

/** Options for {@link createVoipStatsSampler}. Everything is injected so the loop needs no globals. */
export interface IVoipStatsSamplerOptions {
  /** Resolved LIVE, per tick. `null` means "no client" (stop); `[]` means "a client with no peers" (idle). */
  readPeers: () => IVoipStatsPeer[] | null;
  /** The number of remote peers tracked this tick, written-off ones included. */
  onPeerCount: (remotePeers: number) => void;
  onPeerSample: (connectionId: string, sample: IVoipPeerSample) => void;
  /** A `getStats()` read rejected or was not callable. */
  onReadFailure: () => void;
  /**
   * This peer's transport has delivered no bytes for a full window and needs the recovery ladder.
   *
   * Required, not optional: a sampler wired without an actor is the AVS-4441 defect itself — the
   * measurement existed and nothing acted on it. Making the sink mandatory means a future caller
   * has to decide what happens on silence rather than inherit "nothing" by omission.
   */
  onSilentTransport: (connectionId: string) => void;
  /**
   * This peer's video packets arrive but decode to no frame, with its camera on, for a full window
   * (AVS-4458). Called once per window for as long as the stall lasts. Required for the same reason
   * as `onSilentTransport`.
   */
  onStalledVideo: (connectionId: string, evidence: IStalledVideoEvidence) => void;
  /** Frames decode again for a peer that `onStalledVideo` escalated. Called once per escalation. */
  onVideoRecovered: (connectionId: string) => void;
  /**
   * A WebKit receiver track is muted while its media keeps arriving for a full window (AVS-4484).
   * Called once per window for as long as the link stays dead. Required for the same reason as
   * `onSilentTransport`: a measurement with no actor is the AVS-4441 defect.
   */
  onDeadReceiver: (connectionId: string, evidence: IDeadReceiverEvidence) => void;
  intervalMs?: number;
}

/** The sampler's control surface. */
export interface IVoipStatsSampler {
  /** Idempotent. Starts the loop, or un-parks it. */
  arm(): void;
  /** Stops the loop. A read in flight is discarded when it lands. */
  stop(): void;
  /** True while the loop is running. */
  isArmed(): boolean;
}

/**
 * Projects one peer's stats report plus its store-side flags to the sample the counters consume.
 *
 * The one place the raw report is touched. `selectSelectedPair` returns the pair's addresses and
 * TURN url; only the two candidate types (as one boolean) and the RTT survive.
 *
 * @param entries - the plain records of one `RTCStatsReport` (see `statsEntries`)
 * @param flags - the store-side facts the verdict needs, read off the peer entry
 * @param video - the report's inbound video summary, when the caller already walked it
 * @returns {IVoipPeerSample} numbers and booleans only
 */
export function projectVoipPeerSample(
  entries: readonly TRawStat[],
  flags: Pick<IVoipStatsPeer, 'hasStream' | 'videoExpected'>,
  video: IInboundVideo = summarizeInboundVideo(entries)
): IVoipPeerSample {
  const pair = selectSelectedPair(entries);

  return {
    relay: pair.localType === 'relay' || pair.remoteType === 'relay',
    roundTripMs: pair.roundTripMs,
    framesDecoded: video.framesDecoded,
    hasStream: flags.hasStream,
    videoExpected: flags.videoExpected,
  };
}

/**
 * The sampler. Pure apart from `setTimeout`; see the module header for the lifecycle it implements.
 *
 * @param options - the injected reader and sinks
 * @returns {IVoipStatsSampler} a parked sampler; call `arm()` to start it
 */
export function createVoipStatsSampler(options: IVoipStatsSamplerOptions): IVoipStatsSampler {
  const intervalMs = options.intervalMs ?? VOIP_STATS_INTERVAL_MS;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let idleTicks = 0;
  let running = false;
  // Bumped on every `arm()`. `running` alone cannot tell two arm-eras apart: `stop()` can land
  // while a read is in flight and a later `arm()` — a fast rejoin, DCC's immediate session
  // restart, a takeover — flips it back to true before that read resolves.
  let armGeneration = 0;
  // Per-peer silence record: the last inbound byte total seen and how many ticks it has not moved.
  // Keyed by connectionId and pruned every tick against the live peer list, so a peer that leaves
  // (or rejoins) never inherits a stale window.
  const silence = new Map<string, { bytes: number; ticks: number }>();
  // Per-peer stalled-video record (AVS-4458): the pc the totals came from, the last video packet and
  // decoded-frame totals, and how many ticks packets moved while frames did not. Pruned at every
  // site `silence` is.
  const stalledVideo = new Map<
    string,
    { peerConnection: RTCPeerConnection; packets: number; frames: number; ticks: number }
  >();
  // Peers `onStalledVideo` escalated and that have not decoded a frame since — the only peers a
  // decoding tick reports as recovered.
  const videoEscalated = new Set<string>();
  // Per-peer dead-receiver record (AVS-4484): the pc the counters came from, the last counter of every
  // inbound receiver (by track id), and how many ticks a muted track's own counter rose. Keyed on the
  // pc so a replaced pc starts a fresh baseline. Pruned wherever `forgetVideo` is.
  const deadReceiver = new Map<
    string,
    { peerConnection: RTCPeerConnection; counters: ReadonlyMap<string, number>; ticks: number }
  >();

  function forgetVideo(connectionId: string): void {
    stalledVideo.delete(connectionId);
    videoEscalated.delete(connectionId);
    deadReceiver.delete(connectionId);
  }

  /**
   * The live peer list's entry for this connection, on the same pc — re-read after a stats read,
   * because `hasStream`/`videoExpected`/`audioExpected` can change while the read was in flight.
   * `undefined` when the store no longer holds this peer on this pc.
   */
  function livePeer(peer: IVoipStatsPeer): IVoipStatsPeer | undefined {
    return options
      .readPeers()
      ?.find((live) => live.connectionId === peer.connectionId && live.peerConnection === peer.peerConnection);
  }

  /**
   * Banks one tick of transport silence for a peer and says whether the window just closed.
   *
   * @param peer - the peer as the store sees it this tick
   * @param entries - its `getStats()` report, already read
   * @returns {boolean} `true` exactly once per window, on the tick that closes it
   */
  function trackSilence(peer: IVoipStatsPeer, entries: readonly TRawStat[]): boolean {
    const inbound = summarizeInboundBytes(entries);

    // Three peers legitimately receive nothing: one whose `ontrack` never fired, one that publishes
    // nothing (camera denied, send-only), and one that muted BOTH mic and camera (mute keeps the
    // inbound entries, so bytes freeze). Escalating any of them would write off a healthy peer.
    // Deleting the record makes the first unmuted tick a fresh baseline.
    if (!peer.hasStream || inbound.trackCount === 0 || (!peer.audioExpected && !peer.videoExpected)) {
      silence.delete(peer.connectionId);

      return false;
    }

    const previous = silence.get(peer.connectionId);

    if (!previous || previous.bytes !== inbound.bytes) {
      silence.set(peer.connectionId, { bytes: inbound.bytes, ticks: 0 });

      return false;
    }

    const ticks = previous.ticks + 1;

    if (ticks < VOIP_SILENT_TICKS_BEFORE_ESCALATION) {
      silence.set(peer.connectionId, { bytes: inbound.bytes, ticks });

      return false;
    }

    // Escalating drops the record rather than zeroing the counter. The next tick then has nothing
    // to compare against and starts a fresh baseline, so the actor fires once per window — never
    // once per tick for as long as the peer stays silent.
    silence.delete(peer.connectionId);

    return true;
  }

  /**
   * Banks one tick of stalled video for a peer: packets arriving, not one frame decoded.
   *
   * @param peer - the peer as the store sees it this tick
   * @param video - its inbound video summary this tick
   * @returns {'stalled' | 'recovered' | null} `'stalled'` once per window, on the tick that closes
   * it; `'recovered'` on the first decoding tick after an escalation; otherwise `null`
   */
  function trackStalledVideo(peer: IVoipStatsPeer, video: IInboundVideo): 'stalled' | 'recovered' | null {
    // No stream, camera off, or no inbound video entry at all (a camera-denied peer still signals
    // `videoMuted:false`): nothing is owed, so nothing can stall. Deleting the record makes the
    // first tick back a fresh baseline. This deliberately leaves `videoEscalated` alone: a peer
    // already escalated whose camera then goes off (e.g. after the give-up) and later decodes
    // again still reports one recovery on the first tick with frames flowing. The client's
    // `clearStalledVideo` no-ops when it holds no ladder state, so the stray call is harmless.
    if (!peer.hasStream || !peer.videoExpected || video.trackCount === 0) {
      stalledVideo.delete(peer.connectionId);

      return null;
    }

    const previous = stalledVideo.get(peer.connectionId);
    const current = {
      peerConnection: peer.peerConnection,
      packets: video.packetsReceived,
      frames: video.framesDecoded,
      ticks: 0,
    };
    // `rebuildPeerTransport` keeps the connection id and swaps the pc. The client drops this peer's
    // ladder state with the old pc, so the old escalation has no recovery left to report.
    const replaced = previous !== undefined && previous.peerConnection !== peer.peerConnection;

    if (replaced) {
      videoEscalated.delete(peer.connectionId);
    }

    // A new pc or a counter that went backwards describes a different stream than the record, so
    // the change is neither a decoded frame nor packet progress: start a fresh baseline.
    if (!previous || replaced || current.packets < previous.packets || current.frames < previous.frames) {
      stalledVideo.set(peer.connectionId, current);

      return null;
    }

    if (current.frames !== previous.frames) {
      stalledVideo.set(peer.connectionId, current);

      return videoEscalated.delete(peer.connectionId) ? 'recovered' : null;
    }

    // Frames flat AND packets flat is not this ladder's state. A fully silent peer belongs to the
    // silent-transport ladder, and a camera muted at the source (backgrounded iOS Safari, a closed
    // lid) sends no packets and no `videoMuted:true` — escalating it would switch an away user's
    // camera off for everyone.
    if (current.packets === previous.packets) {
      stalledVideo.set(peer.connectionId, current);

      return null;
    }

    const ticks = previous.ticks + 1;

    if (ticks < VOIP_STALLED_VIDEO_TICKS_BEFORE_ESCALATION) {
      stalledVideo.set(peer.connectionId, { ...current, ticks });

      return null;
    }

    // Once per window, never once per tick: the count restarts, but the totals stay as the next
    // baseline, so a frame the ICE restart decodes before the next tick still reads as a recovery.
    stalledVideo.set(peer.connectionId, current);
    videoEscalated.add(peer.connectionId);

    return 'stalled';
  }

  /**
   * Banks one tick of a WebKit receiver that stays muted while its media flows (AVS-4484).
   *
   * @param peer - the peer as the store sees it this tick
   * @param video - its inbound video summary this tick
   * @param audio - its inbound audio summary this tick
   * @returns {IDeadReceiverEvidence | null} the evidence once per window, on the tick that closes it
   */
  function trackDeadReceiver(
    peer: IVoipStatsPeer,
    video: IInboundVideo,
    audio: IInboundAudio,
    counters: ReadonlyMap<string, number>
  ): IDeadReceiverEvidence | null {
    const reported = reportedMutedKinds(peer.mutedTracks, counters);
    const videoSuspect = peer.videoExpected && reported.video;
    const audioSuspect = peer.audioExpected && reported.audio;

    // A muted track with nothing flowing, a sender that muted, or no stream is another state's
    // business. Deleting the record makes the next suspect tick a fresh baseline.
    if (!peer.hasStream || (!videoSuspect && !audioSuspect)) {
      deadReceiver.delete(peer.connectionId);

      return null;
    }

    const previous = deadReceiver.get(peer.connectionId);
    const current = { peerConnection: peer.peerConnection, counters, ticks: 0 };

    // A new pc or a counter that went backwards describes a different stream than the record.
    if (
      !previous ||
      previous.peerConnection !== peer.peerConnection ||
      [...counters].some(([id, value]) => value < (previous.counters.get(id) ?? 0))
    ) {
      deadReceiver.set(peer.connectionId, current);

      return null;
    }

    const rising = risingMutedKinds(peer.mutedTracks, previous.counters, counters);
    const videoRising = videoSuspect && rising.video;
    const audioRising = audioSuspect && rising.audio;

    // Flat counters are a sender that stopped, not a dead receiver.
    if (!videoRising && !audioRising) {
      deadReceiver.set(peer.connectionId, current);

      return null;
    }

    const ticks = previous.ticks + 1;

    if (ticks < VOIP_DEAD_RECEIVER_TICKS_BEFORE_ESCALATION) {
      deadReceiver.set(peer.connectionId, { ...current, ticks });

      return null;
    }

    // Once per window: the count restarts and the counters stay as the next baseline.
    deadReceiver.set(peer.connectionId, current);

    return {
      kinds: { audio: audioRising, video: videoRising },
      framesDecoded: video.framesDecoded,
      audioBytes: audio.bytesReceived,
    };
  }

  function schedule(): void {
    if (timer !== null) {
      return;
    }

    timer = setTimeout(() => {
      timer = null;
      const generation = armGeneration;

      // The tick's own `schedule()` is unreachable if anything in it throws — `timer` is already
      // null and every later `arm()` early-returns on `running`, so the loop would be a zombie for
      // the rest of the tab, freezing `statsSamples` and under-reporting the SESSION_ENDED
      // counters. Rescheduling here keeps a surprise from ending the loop; the era guard is the
      // same one the tick uses, so a `stop()` that raced the throw is still respected.
      void tick().catch(() => {
        if (running && armGeneration === generation) {
          schedule();
        }
      });
    }, intervalMs);
  }

  async function tick(): Promise<void> {
    if (!running) {
      return;
    }

    const generation = armGeneration;
    const peers = options.readPeers();

    if (peers === null) {
      stop();
      return;
    }

    // A peer that left the store keeps no window. Without this a rejoin — a reload, a takeover —
    // would inherit the old peer's banked silence and escalate on its very first tick.
    const livePeerIds = new Set(peers.map((peer) => peer.connectionId));

    for (const connectionId of silence.keys()) {
      if (!livePeerIds.has(connectionId)) {
        silence.delete(connectionId);
      }
    }

    for (const connectionId of new Set([...stalledVideo.keys(), ...videoEscalated, ...deadReceiver.keys()])) {
      if (!livePeerIds.has(connectionId)) {
        forgetVideo(connectionId);
      }
    }

    if (peers.length === 0) {
      idleTicks += 1;

      if (idleTicks >= VOIP_STATS_IDLE_TICKS_BEFORE_PARK) {
        stop();
        return;
      }

      schedule();
      return;
    }

    idleTicks = 0;
    options.onPeerCount(peers.length);

    for (const peer of peers) {
      if (peer.transportFailed) {
        silence.delete(peer.connectionId);
        forgetVideo(peer.connectionId);
        continue;
      }

      const entries = await readStatsEntries(peer.peerConnection);

      // `stop()` can land while a read is in flight — an uninstall, a session end, a takeover —
      // and a later `arm()` can open a new era before this read resolves. Either way the era this
      // result belongs to is gone: it belongs to nobody, and rescheduling from here would arm a
      // timer nothing owns any more. Comparing the generation, not just `running`, is what keeps
      // the old client's samples out of the new session's tally.
      if (!running || armGeneration !== generation) {
        return;
      }

      if (entries === null) {
        options.onReadFailure();
        continue;
      }

      // The pc can change during the read too: `rebuildPeerTransport` swaps it under the same
      // connection id, and a peer can leave. A result from a pc the store no longer holds describes
      // nobody, so it reaches no sink and no window. Use the live entry from here on — the store's
      // flags (hasStream, videoExpected, audioExpected) can have changed during the await.
      const current = livePeer(peer);

      if (!current) {
        continue;
      }

      const video = summarizeInboundVideo(entries);

      options.onPeerSample(current.connectionId, projectVoipPeerSample(entries, current, video));

      if (trackSilence(current, entries)) {
        options.onSilentTransport(current.connectionId);
      }

      const videoVerdict = trackStalledVideo(current, video);

      if (videoVerdict === 'stalled') {
        options.onStalledVideo(current.connectionId, {
          framesDecoded: video.framesDecoded,
          framesReceived: video.framesReceived,
          packetsReceived: video.packetsReceived,
        });
      } else if (videoVerdict === 'recovered') {
        options.onVideoRecovered(current.connectionId);
      }

      const dead = trackDeadReceiver(current, video, summarizeInboundAudio(entries), inboundCountersByTrack(entries));

      if (dead) {
        options.onDeadReceiver(current.connectionId, dead);
      }
    }

    schedule();
  }

  function stop(): void {
    running = false;
    idleTicks = 0;
    silence.clear();
    stalledVideo.clear();
    videoEscalated.clear();
    deadReceiver.clear();

    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  return {
    arm(): void {
      if (running) {
        return;
      }

      running = true;
      idleTicks = 0;
      armGeneration += 1;
      schedule();
    },
    stop,
    isArmed(): boolean {
      return running;
    },
  };
}

/**
 * One `getStats()` read that reports its own failure instead of throwing.
 *
 * A pc that closed under the read rejects; a mock or a foreign object has no callable `getStats`.
 * Both are "this read failed", never "this peer is silent" — conflating the two is how a counter
 * invents an outage. Only the call itself is inside the try.
 */
async function readStatsEntries(peerConnection: RTCPeerConnection): Promise<TRawStat[] | null> {
  let report: RTCStatsReport;

  try {
    report = await peerConnection.getStats();
  } catch {
    return null;
  }

  return statsEntries(report);
}

/**
 * The live client, or `null`.
 *
 * `RTCClient.current()` rather than `getInstance()`: an observer must never be the reason a client
 * exists. The `typeof` guard is load-bearing — `utils/ui/rtc.test.ts` mocks `webrtc` as
 * `{ RTCClient: { getInstance } }` with no `current`, and a tick landing inside that suite must read
 * as "no client", never throw inside a timer callback.
 */
function currentClient(): RTCClient | null {
  const current: unknown = RTCClient.current;

  return typeof current === 'function' ? RTCClient.current() : null;
}

/**
 * The LIVE peer list, re-resolved per tick from the current client's store.
 *
 * @returns {IVoipStatsPeer[] | null} `null` when there is no client; otherwise every store entry
 * that holds a peer connection, with the three store-side facts the verdict needs
 */
export function readLiveVoipPeers(): IVoipStatsPeer[] | null {
  const client = currentClient();

  if (!client) {
    return null;
  }

  const remoteConnections = client.store.get('remoteConnections') ?? {};

  return Object.values(remoteConnections).flatMap((connection) =>
    connection.peerConnection
      ? [
          {
            connectionId: connection.connectionId,
            peerConnection: connection.peerConnection,
            transportFailed: !!connection.transportFailed,
            hasStream: !!connection.stream,
            audioExpected: connection.audioMuted !== true,
            videoExpected: connection.videoMuted !== true,
            mutedTracks: mutedReceiverTrackIds(connection.stream),
          },
        ]
      : []
  );
}

/**
 * Hands one silent transport to the recovery ladder on the live client.
 *
 * The client owns the escalation policy — how many windows a peer gets, which rung it climbs, when
 * it is written off. This module only reports that the bytes stopped. Resolving the client per call
 * (never caching it) is the same rule the tick follows: `destroy()` nulls the singleton and a
 * takeover rebuilds it.
 *
 * @param connectionId - the peer whose transport went silent
 */
export function escalateSilentTransport(connectionId: string): void {
  currentClient()?.escalateSilentTransport(connectionId);
}

/**
 * Hands one stalled-video peer to its recovery ladder on the live client (AVS-4458). Same
 * resolve-per-call rule as {@link escalateSilentTransport}.
 *
 * @param connectionId - the peer whose video packets arrive but never decode
 * @param evidence - the inbound video counters on the escalating tick
 */
export function escalateStalledVideo(connectionId: string, evidence: IStalledVideoEvidence): void {
  currentClient()?.escalateStalledVideo(connectionId, evidence);
}

/**
 * Tells the live client that an escalated peer decodes frames again, so its ladder can stand down.
 *
 * @param connectionId - the peer whose video recovered
 */
export function clearStalledVideo(connectionId: string): void {
  currentClient()?.clearStalledVideo(connectionId);
}

/**
 * Hands one dead-receiver peer to its recovery ladder on the live client (AVS-4484). Same
 * resolve-per-call rule as {@link escalateSilentTransport}.
 *
 * @param connectionId - the peer whose receivers are muted while their media flows
 * @param evidence - the counters the sampler read on the escalating tick
 */
export function escalateDeadReceiver(connectionId: string, evidence: IDeadReceiverEvidence): void {
  currentClient()?.escalateDeadReceiver(connectionId, evidence);
}

/**
 * The one sampler the app runs. Module scope, like `voipHandlerUnsubscribes` in `utils/ui/rtc.ts`:
 * `initWebRtc` arms it after every successful connect and `session:end` stops it, and because it
 * re-resolves the client per tick it follows a takeover's rebuilt client on its own.
 */
export const voipStatsSampler: IVoipStatsSampler = createVoipStatsSampler({
  readPeers: readLiveVoipPeers,
  onPeerCount: recordVoipPeerCount,
  onPeerSample: recordVoipPeerSample,
  onReadFailure: recordVoipStatsReadFailure,
  onSilentTransport: escalateSilentTransport,
  onStalledVideo: escalateStalledVideo,
  onVideoRecovered: clearStalledVideo,
  onDeadReceiver: escalateDeadReceiver,
});
